import { FC, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Redirect, Route, Switch, useHistory } from 'react-router-dom'
import { useIdleTimer } from 'react-idle-timer'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import CaseLevelContent from 'components/features/dashboard/CaseLevelContent'
import CaseLevelNavbar from 'components/features/dashboard/CaseLevelNavbar'
import ClientLevelDashboard from 'components/features/client/ClientLevelDashboard'
import NewAccountMain from 'components/features/accountcreation/NewAccountMain'
import LoginPageMain from 'components/features/login/LoginPageMain'
import Footer from 'components/molecules/Footer'
import UserProfileMain from 'components/features/userprofile/UserProfileMain'
import NotificationsList from 'components/features/NotificationsList'
import PrivateRoute from '../privateroute/PrivateRoute'
import ToastNotification from 'components/molecules/ToastNotification'
import ModalTimeout from 'components/molecules/ModalTimeout'
import { logOut } from 'share/utils/authenticationUtils'
import { SignOffResponse, SIGN_OFF } from 'share/mutations/authentication'
import LoadingSpinner from 'components/atoms/LoadingSpinner'

import { MainContentContainer, MainContentWrapper } from './styles'

const vhCalculate = () => {
  const vh = window.innerHeight * 0.01
  document.documentElement.style.setProperty('--vh', `${vh}px`)
}

const MainContent: FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const authentication = useAppSelector((state) => state.authentication)
  const [isVisible, setIsVisble] = useState(false)
  const [signOff, { loading }] = useMutation<SignOffResponse>(SIGN_OFF)

  const loadingDelay = useLoadingDelay(loading)

  const handleOnIdle = async () => {
    if (authentication.isAuthenticated) {
      await signOff()
      await logOut(dispatch)
      setIsVisble(true)
    }
  }

  useIdleTimer({
    timeout: 1000 * 60 * 15,
    onIdle: handleOnIdle,
    debounce: 500
  })

  useEffect(() => {
    window.addEventListener('resize', vhCalculate)

    return () => {
      window.removeEventListener('resize', vhCalculate)
    }
  }, [])

  return (
    <>
      <MainContentWrapper className='main-content-wrapper'>
        {loadingDelay && <LoadingSpinner />}
        <MainContentContainer className='main-content-container'>
          <Switch>
            <Route path='/account-creation/:invitationId' exact>
              <NewAccountMain />
            </Route>
            <Route path='/login'>
              <LoginPageMain />
            </Route>
            {/* Temporary path to dev out the chatbot components */}
            <PrivateRoute path='/user-profile' exact>
              <UserProfileMain />
            </PrivateRoute>
            <PrivateRoute path='/notifications' exact>
              <NotificationsList />
            </PrivateRoute>
            <PrivateRoute path='/client' exact>
              <ClientLevelDashboard />
            </PrivateRoute>
            <PrivateRoute path='/dashboard/case'>
              <CaseLevelNavbar />
              <CaseLevelContent />
            </PrivateRoute>
            <PrivateRoute path='/dashboard/chatbot'>
              <CaseLevelNavbar />
              <CaseLevelContent />
            </PrivateRoute>
            <PrivateRoute path='/dashboard/caseinformation'>
              <CaseLevelNavbar />
              <CaseLevelContent />
            </PrivateRoute>
            <PrivateRoute path='/dashboard/lawfirminformation'>
              <CaseLevelNavbar />
              <CaseLevelContent />
            </PrivateRoute>
            <PrivateRoute path='/dashboard/frequentlyaskedquestions'>
              <CaseLevelNavbar />
              <CaseLevelContent />
            </PrivateRoute>
            <PrivateRoute path='/dashboard/documents'>
              <CaseLevelNavbar />
              <CaseLevelContent />
            </PrivateRoute>
            <PrivateRoute path='/dashboard/courses'>
              <CaseLevelNavbar />
              <CaseLevelContent />
            </PrivateRoute>
            <PrivateRoute path='/dashboard/questionnaires'>
              <CaseLevelNavbar />
              <CaseLevelContent />
            </PrivateRoute>
            <PrivateRoute path='/dashboard/billing'>
              <CaseLevelNavbar />
              <CaseLevelContent />
            </PrivateRoute>
            <Redirect from='/' to='/dashboard/case' exact />
            <Redirect from='/dashboard' to='/dashboard/case' exact />
          </Switch>
          <Footer />
          <ToastNotification />
          <ModalTimeout
            show={isVisible}
            onHide={() => setIsVisble(false)}
            onContinue={() => {
              history.push('/login')
              window.location.reload()
              setIsVisble(false)
            }}
          />
        </MainContentContainer>
      </MainContentWrapper>
    </>
  )
}

export default MainContent
